import { useCallback } from 'react'
import { api } from '../api/client.js'

export interface TenantAdmin {
  id: string
  username?: string
  firstName?: string
  middleName?: string
  lastName?: string
  email: string
  mobileNumber?: string
  landlineNumber?: string
  status: boolean
  createdAt: string
  tenantId?: string
  tenantName?: string
  privilegeName?: string
}

export interface TenantAdminFormData {
  username?: string
  firstName: string
  middleName?: string
  lastName: string
  email: string
  password?: string
  mobileNumber?: string
  landlineNumber?: string
  status: boolean
  domainId?: string
  privilegeId?: string
}

export interface UseTenantAdminsResult {
  list: (limit?: number, offset?: number) => Promise<{ admins: TenantAdmin[]; stats: any }>
  get: (id: string) => Promise<TenantAdmin>
  create: (data: TenantAdminFormData) => Promise<TenantAdmin>
  update: (id: string, data: TenantAdminFormData) => Promise<TenantAdmin>
  remove: (id: string) => Promise<void>
  resetMfa: (id: string, password: string, loginId?: string) => Promise<void>
}

export function useTenantAdmins(): UseTenantAdminsResult {
  const list = useCallback(async (limit = 100, offset = 0) => {
    return api.get<{ admins: TenantAdmin[]; stats: any }>(
      `/platform/tenant-admins?limit=${limit}&offset=${offset}`
    )
  }, [])

  const get = useCallback(async (id: string) => {
    const res = await api.get<{ admin: TenantAdmin }>(`/platform/tenant-admins/${id}`)
    return res.admin
  }, [])

  const create = useCallback(async (data: TenantAdminFormData) => {
    const res = await api.post<{ admin: TenantAdmin }>('/platform/tenant-admins', data)
    return res.admin
  }, [])

  const update = useCallback(async (id: string, data: TenantAdminFormData) => {
    const res = await api.put<{ admin: TenantAdmin }>(`/platform/tenant-admins/${id}`, data)
    return res.admin
  }, [])

  const remove = useCallback(async (id: string) => {
    await api.delete(`/platform/tenant-admins/${id}`)
  }, [])

  const resetMfa = useCallback(async (id: string, password: string, loginId?: string) => {
    await api.post(`/platform/tenant-admins/${id}/reset-mfa`, { loginId, password })
  }, [])

  return { list, get, create, update, remove, resetMfa }
}
